import {
	DEFAULT_DATE_QUERY_FORMAT,
	convertToValidCustomRange,
	getLast30DaysDateRange,
	getLast7DaysDateRange,
	getLast90DaysDateRange,
	getLastYearDateRange,
	getTodayDateRange,
	getYesterdayDateRange,
} from '@/shared/utils/date-range';
import { CustomDateRange } from '@/shared/types/date';
import { CompletedCyclesTypeFilter } from '@/api/dashboard/types';
import dayjs from 'dayjs';
import { FILE_TITLE_DATE_FORMAT } from '@/shared/constants/date';
import { QueryDateRange, DateFilter } from './types';

export const TYPE_FILTER_OPTIONS = [
	{
		value: CompletedCyclesTypeFilter.LOCATIONS,
		label: 'Location',
	},
	{
		value: CompletedCyclesTypeFilter.USERS,
		label: 'User',
	},
	{
		value: CompletedCyclesTypeFilter.DEVICES,
		label: 'Device',
	},
];

export const DATE_FILTER_OPTIONS = Object.values(DateFilter).map((dateFilterOption) => ({
	value: dateFilterOption,
	label: dateFilterOption,
}));

export const getPredefinedDateRange = (dateFilterOption: DateFilter): CustomDateRange | null => {
	switch (dateFilterOption) {
		case DateFilter.TODAY:
			return getTodayDateRange();
		case DateFilter.YESTERDAY:
			return getYesterdayDateRange();
		case DateFilter.LAST_7_DAYS:
			return getLast7DaysDateRange();
		case DateFilter.LAST_30_DAYS:
			return getLast30DaysDateRange();
		case DateFilter.LAST_90_DAYS:
			return getLast90DaysDateRange();
		case DateFilter.LAST_YEAR:
			return getLastYearDateRange();
		default:
			return null;
	}
};

export const convertDateFilter = (
	dateFilter: DateFilter,
	customDateRange: CustomDateRange
): QueryDateRange => {
	const dateRange: CustomDateRange =
		dateFilter === DateFilter.CUSTOM_RANGE
			? convertToValidCustomRange(customDateRange)
			: getPredefinedDateRange(dateFilter) || { start: null, end: null };

	return {
		start: dateRange.start?.format(DEFAULT_DATE_QUERY_FORMAT),
		end: dateRange.end?.format(DEFAULT_DATE_QUERY_FORMAT),
	};
};

export const getStatisticsExportFileName = (
	base: string,
	dateRange: QueryDateRange,
	desc?: string
) => {
	const formattedStartDate = dayjs(dateRange.start).format(FILE_TITLE_DATE_FORMAT);
	const formattedEndDate = dayjs(dateRange.end).format(FILE_TITLE_DATE_FORMAT);
	const titleRange =
		formattedStartDate === formattedEndDate
			? formattedStartDate
			: `${formattedStartDate}-${formattedEndDate}`;
	const middlePart = desc ? `-[${desc}]` : '';
	return `${base}${middlePart}-[${titleRange}].csv`;
};

export function getPercentColor(percent: number, isErrorRate = false): string {
	if (isErrorRate) {
		if (percent <= 10) return '#11AA35';
		if (percent <= 20) return '#FF933A';
		return '#FF4B4B';
	}
	if (percent >= 91) return '#11AA35';
	if (percent >= 81) return '#FF933A';
	return '#FF4B4B';
}

export function getPercent(count: number, count_2: number): number {
	return count > 0 ? Math.round((count_2 / count) * 100) : 0;
}

export function formatNumber(value: number | undefined | null): string {
	if (typeof value !== 'number' || Number.isNaN(value)) return '';
	return Math.round(value).toLocaleString('en-US');
}
